import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSection,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { NumericInput } from '@/components/inputs/NumericInput'
import {
  BulkBundlePriceList,
  BundlePriceItem,
} from '@/features/admin/bundle/components/BulkBundlePriceList'
import type { Id } from '@/infra/id'

export interface BulkEditPriceDialogBundle {
  id: Id
  name: string
  price: number
  newPrice: number | null
}

export interface BulkEditPriceDialogProps {
  showing: boolean
  onClose: () => unknown
  bundles: BulkEditPriceDialogBundle[]
  onNewPriceChange: (bundleId: Id, price: number | null) => void
  hasEligibleBundles: boolean
  hasArchivedBundles: boolean
  archivedCount: number
  canApply: boolean
  onSubmit: () => unknown
  isSubmitPending: boolean
  testId?: string
}

export function BulkEditPriceDialog({
  showing,
  onClose,
  bundles,
  onNewPriceChange,
  hasEligibleBundles,
  hasArchivedBundles,
  archivedCount,
  canApply,
  onSubmit,
  isSubmitPending,
  testId,
}: BulkEditPriceDialogProps) {
  return (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Edit price</ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <BulkBundlePriceList rightHeader="New price">
          {bundles.map((bundle) => (
            <BundlePriceItem
              key={bundle.id}
              name={bundle.name}
              currentPrice={bundle.price}
              testId={testId && `${testId}-row-${bundle.name}`}
              right={
                <NumericInput
                  aria-label={`New price for ${bundle.name}`}
                  mode="draft"
                  precision={2}
                  currencyCode="USD"
                  value={bundle.newPrice}
                  onChange={(price) => onNewPriceChange(bundle.id, price)}
                  testId={testId && `${testId}-input-${bundle.name}`}
                />
              }
            />
          ))}
        </BulkBundlePriceList>
      </ModalSheetContent>

      <ModalSheetFooter>
        {!hasEligibleBundles ? (
          <ModalSheetFooterSection>
            All selected bundles are archived and can’t be repriced.
          </ModalSheetFooterSection>
        ) : (
          hasArchivedBundles && (
            <ModalSheetFooterSection>
              {archivedCount} archived{' '}
              {archivedCount === 1 ? 'bundle' : 'bundles'} excluded.
            </ModalSheetFooterSection>
          )
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            isDisabled={isSubmitPending}
            testId={testId && `${testId}-close-button`}
          />

          <Button
            label="Save prices"
            variant="primary"
            onPress={onSubmit}
            isPending={isSubmitPending}
            isDisabled={!canApply || isSubmitPending}
            testId={testId && `${testId}-submit-button`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
